import { useState } from "react";
import { fetchData } from "./fetch";
import { getSecretKey } from "./keyGenerator";
import { getMockData } from "./mock";

const API_URL = process.env.REACT_APP_API_URL || "--NO-URL--";

export type TrackingItemType = {
  articleNo: string;
  deliveryStatus: number;
  orderNo: number;
  dateExpected?: string;
  dateStorage?: string;
  amountDelivered: number;
  amountPending: number;
  amountInitial?: number;
  amountOrdered: number;
  salesUnit: string;
  articleName: string;
  requestedDeliveryDate?: string;
  trackingURL?: string;
  habblImageURL?: string;
  habblDeliveryDate?: string;
  habblGeoCoords?: string;
  backorderPos?: Array<TrackingItemType>;
  fixedItemID: number;
  itemID: number;
  backorderItemID?: number;
  deliveryNoteNumber?: string;
};

type ShippingAddressType = {
  company1: string;
  company2?: string;
  company3?: string;
  company4?: string;
  street?: string;
  postalCode?: string;
  city?: string;
  country?: string;
};

export type GetOrderResponseType = {
  errorMsg?: string;
  shippingAddress?: ShippingAddressType;
  orderDate?: string;
  dateExpected?: string;
  shippingMethodID?: number;
  orderID?: string;
  orderPos?: Array<TrackingItemType>;
};

type GetOrderInputType = {
  customerID: string;
  orderID: number;
  secret: string;
};

const ENV = process.env.REACT_APP_ENVIRONMENT || "NONE";

export const useGetOrder = () => {
  const [data, setData] = useState<GetOrderResponseType | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);

  const clear = () => {
    setData(undefined);
    setError(false);
  };

  const call = (props: Pick<GetOrderInputType, "customerID" | "orderID">) => {
    const { customerID, orderID } = props;
    if (!customerID) {
      return;
    }
    if (!orderID) {
      return;
    }
    if (customerID === "") {
      return;
    }
    if (isNaN(orderID)) {
      return;
    }
    setLoading(true);
    setError(false);
    getOrder({ ...props })
      .then((res) => {
        if (res.errorMsg && res.errorMsg !== "") {
          setError(true);
          setData(undefined);
          return;
        }
        setData(res);
      })
      .finally(() => {
        setLoading(false);
      })
      .catch(() => {
        setError(true);
        setData(undefined);
      });
  };

  return { data, loading, error, call, clear };
};

const getOrder = async (
  props: Pick<GetOrderInputType, "customerID" | "orderID">,
): Promise<GetOrderResponseType> => {
  const { customerID, orderID } = props;
  const secret = await getSecretKey();
  const data: GetOrderInputType = { customerID, orderID, secret };

  if (ENV === "DEV" && orderID === 123 && customerID === "123") {
    return getMockData();
  }

  return await fetchData<GetOrderInputType, GetOrderResponseType>({
    url: API_URL,
    data,
  });
};
